import { loadScript } from 'dashboard/helper/DOMHelpers';

const OAUTH_REDIRECT_URI_STORAGE_KEY =
  'chatwoot:whatsapp_embedded_signup:redirect_uri';

const findOAuthRedirectUri = (rawUrl, depth = 0) => {
  if (!rawUrl || depth > 2) return '';

  try {
    const url = new URL(rawUrl, window.location.href);
    const redirectUri = url.searchParams.get('redirect_uri');
    if (redirectUri) return redirectUri;

    const nextUrl = url.searchParams.get('next');
    return nextUrl ? findOAuthRedirectUri(nextUrl, depth + 1) : '';
  } catch {
    return '';
  }
};

const rememberOAuthRedirectUri = rawUrl => {
  const redirectUri = findOAuthRedirectUri(rawUrl);
  if (!redirectUri) return;

  try {
    window.localStorage.setItem(OAUTH_REDIRECT_URI_STORAGE_KEY, redirectUri);
  } catch {
    // Ignore storage errors.
  }
};

export const loadFacebookSdk = async () => {
  return loadScript('https://connect.facebook.net/en_US/sdk.js', {
    async: true,
    defer: true,
    crossOrigin: 'anonymous',
  });
};

export const initializeFacebook = (appId, apiVersion) => {
  const version = apiVersion || 'v22.0';
  return new Promise(resolve => {
    const init = () => {
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version,
      });
      resolve();
    };

    if (window.FB) {
      init();
    } else {
      window.fbAsyncInit = init;
    }
  });
};

export const isValidBusinessData = businessData => {
  return businessData && businessData.business_id && businessData.waba_id;
};

export const createMessageHandler = onEmbeddedSignupData => {
  return event => {
    try {
      const origin = new URL(event.origin);
      if (
        origin.protocol !== 'https:' ||
        (origin.hostname !== 'facebook.com' &&
          !origin.hostname.endsWith('.facebook.com'))
      ) {
        return;
      }

      let data;
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      } else if (typeof event.data === 'object' && event.data !== null) {
        data = event.data;
      } else {
        return;
      }

      if (data.type === 'WA_EMBEDDED_SIGNUP') {
        onEmbeddedSignupData(data);
      }
    } catch {
      // Ignore non-JSON or irrelevant messages
    }
  };
};

export const initWhatsAppEmbeddedSignup = configId => {
  const LOGIN_TIMEOUT_MS = 120000;

  return new Promise((resolve, reject) => {
    let settled = false;
    try {
      window.localStorage.removeItem(OAUTH_REDIRECT_URI_STORAGE_KEY);
    } catch {
      // Storage may be unavailable in private browsing.
    }
    const originalWindowOpen = window.open;
    const restoreWindowOpen = () => {
      window.open = originalWindowOpen;
    };

    window.open = function captureFacebookOAuthWindow(url, ...args) {
      rememberOAuthRedirectUri(url);
      return originalWindowOpen.apply(window, [url, ...args]);
    };
    const timeout = setTimeout(() => {
      if (!settled) {
        settled = true;
        restoreWindowOpen();
        reject(
          new Error(
            'Facebook login timed out. Check that your domain is whitelisted in the Facebook App settings.'
          )
        );
      }
    }, LOGIN_TIMEOUT_MS);

    try {
      window.FB.login(
        response => {
          if (settled) return;
          settled = true;
          restoreWindowOpen();
          clearTimeout(timeout);

          if (response.authResponse && response.authResponse.code) {
            resolve(response.authResponse.code);
          } else if (response.error) {
            reject(new Error(response.error));
          } else {
            reject(new Error('Login cancelled'));
          }
        },
        {
          config_id: configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: {},
          },
        }
      );
    } catch (error) {
      settled = true;
      restoreWindowOpen();
      clearTimeout(timeout);
      reject(error);
    }
  });
};

export const setupFacebookSdk = async (appId, apiVersion) => {
  const version = apiVersion || 'v22.0';
  await loadFacebookSdk();
  await initializeFacebook(appId, version);
};
